import { create } from 'zustand';
import { useEffect } from 'react';
import { cn } from '@shared/lib/cn';
import styles from './Toast.module.css';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

interface ToastItem {
  readonly id: string;
  readonly tone: ToastTone;
  readonly message: string;
  readonly timeoutMs: number;
}

interface ToastStore {
  readonly items: readonly ToastItem[];
  readonly push: (input: { tone?: ToastTone; message: string; timeoutMs?: number }) => string;
  readonly dismiss: (id: string) => void;
}

/**
 * A toast id.
 *
 * `crypto.randomUUID` is secure-context-only: it is undefined over plain http on
 * a LAN IP, which is exactly how the team tests a Vite dev server. Calling it
 * there threw, and because `toast.success(...)` runs before the rest of a
 * mutation's success handler, the throw took the handler down with it — a key
 * would be created with no dialog, no toast, no sign anything had happened.
 * Only the host differed, so it looked like "works for me, not for anyone else".
 *
 * The id is only ever a React key and a dismiss handle, so uniqueness in-process
 * is enough; it never leaves the browser.
 */
let toastSeq = 0;
function nextToastId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid !== undefined) return uuid;
  toastSeq += 1;
  return `toast-${String(toastSeq)}`;
}

const useToastStore = create<ToastStore>((set) => ({
  items: [],
  push: ({ tone = 'info', message, timeoutMs = 5000 }) => {
    const id = nextToastId();
    set((s) => ({ items: [...s.items, { id, tone, message, timeoutMs }] }));
    return id;
  },
  dismiss: (id) => {
    set((s) => ({ items: s.items.filter((t) => t.id !== id) }));
  },
}));

/** Imperative API for non-React callers (logger, query error handlers). */
export const toast = {
  info: (message: string): string => useToastStore.getState().push({ message, tone: 'info' }),
  success: (message: string): string => useToastStore.getState().push({ message, tone: 'success' }),
  warning: (message: string): string => useToastStore.getState().push({ message, tone: 'warning' }),
  danger: (message: string): string => useToastStore.getState().push({ message, tone: 'danger' }),
  dismiss: (id: string): void => {
    useToastStore.getState().dismiss(id);
  },
};

/** Mount once near the root inside Providers. Renders into a polite live region. */
export function ToastViewport(): JSX.Element {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    const timers = items.map((t) =>
      window.setTimeout(() => {
        dismiss(t.id);
      }, t.timeoutMs),
    );
    return () => {
      timers.forEach((id) => {
        window.clearTimeout(id);
      });
    };
  }, [items, dismiss]);

  return (
    <ol className={styles.viewport} aria-live="polite" aria-label="Notifications">
      {items.map((t) => (
        <li key={t.id} className={cn(styles.toast, styles[`tone_${t.tone}`])}>
          <span className={styles.message}>{t.message}</span>
          <button
            type="button"
            className={styles.dismiss}
            onClick={() => {
              dismiss(t.id);
            }}
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </li>
      ))}
    </ol>
  );
}
